// @ts-nocheck
/*
 * Port of v1 views/dashboard/components/CallQualityDetail.js (simple view).
 *
 * CDR call-detail quality block. Shows a quality word badge, the four
 * plain-language tiles and up to three "what went wrong" lines. The deeper
 * telemetry (charts, audio path, agent-side DSP stats) is still collected
 * and stored by the API — it is simply not rendered for an end customer.
 * Same lookup logic as before: walk every candidate id until one matches.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { getApiCall } from '@/service/httpCall';
import { API_CALL_QUALITY_BY_CALL } from '@/service/url';
import { WARNING_TEXT, mosQuality } from '@/utilities/callQualityRating';
import { QualityTiles } from '@/components/partials/components/CallQualitySimple';

function CallQualityDetail({ callSid, showEmptyState = false, altIds = [] }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // The CDR's call_sid is switch-side while the browser records SIP.js's session
  // id, so try every identifier this row carries (trace id, channel ids, …) until
  // one matches. The endpoint itself also ORs across the ids it stored.
  const candidateIds = useMemo(() => {
    const ids = [callSid, ...(Array.isArray(altIds) ? altIds : [])]
      .map((v: any) => (v === null || v === undefined ? '' : String(v).trim()))
      .filter(Boolean);
    return Array.from(new Set(ids));
  }, [callSid, altIds]);

  useEffect(() => {
    if (!candidateIds.length) {
      setData(null);
      return undefined;
    }
    let mounted = true;
    setLoading(true);

    (async () => {
      let found = null;
      for (const id of candidateIds) {
        try {
          // getApiCall already unwraps to response.data.data; the API sends null
          // there when the call has no WebRTC quality row.
          const resp = await getApiCall(API_CALL_QUALITY_BY_CALL(id));
          if (resp) {
            found = resp;
            break;
          }
        } catch (e) {
          // 403 / network — try the next identifier.
        }
      }
      if (!mounted) return;
      setData(found);
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateIds.join('|')]);

  if (loading) {
    return (
      <div style={{ padding: 12, fontSize: 12, opacity: 0.7 }}>Loading call quality…</div>
    );
  }

  // No row. For a call that was clearly a WebRTC leg the caller passes
  // showEmptyState so the absence is explained rather than looking like a
  // missing feature; otherwise stay silent (mobile / CDR-only calls).
  if (!data) {
    if (!showEmptyState) return null;
    return (
      <div
        style={{
          border: '1px dashed #81818155',
          borderRadius: 12,
          padding: 14,
          margin: '12px 0',
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Call Quality</div>
        <div style={{ opacity: 0.8 }}>
          No quality data was recorded for this call. Quality is measured only on calls
          made from the browser softphone.
        </div>
      </div>
    );
  }

  const quality = mosQuality(data.mos_avg);
  // Only the headline issues — the full warning list stays in the DB.
  const warnings = (Array.isArray(data.warnings) ? data.warnings : [])
    .filter((code: any) => WARNING_TEXT[code])
    .slice(0, 3);

  return (
    <div
      className="call-quality-detail"
      style={{
        border: '1px solid #81818133',
        borderRadius: 12,
        padding: 14,
        margin: '12px 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <h6 style={{ margin: 0, fontWeight: 700 }}>Call Quality</h6>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: quality.color,
            border: `1px solid ${quality.color}66`,
            background: `${quality.color}14`,
            borderRadius: 999,
            padding: '2px 10px',
          }}
        >
          {quality.label}
        </span>
      </div>

      <QualityTiles source={data} />

      {warnings.length ? (
        <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5 }}>
          <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.6, marginBottom: 2 }}>
            WHAT WENT WRONG
          </div>
          {warnings.map((code: any) => (
            <div key={code} style={{ opacity: 0.85 }}>
              • {WARNING_TEXT[code]}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default React.memo(CallQualityDetail);
